import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward,
  CasinoOutlined,
  ChevronRight,
  EmojiEventsOutlined,
  ImageNotSupportedOutlined,
  PersonOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactGA from 'react-ga';
import { serverEnvironment } from 'configs/app-config.js';
import { v2Tokens } from 'theme/v2Theme';
import FeaturedHeroArtwork, {
  featuredHeroActionsSx,
  featuredHeroContentSx,
  featuredHeroPanelSx,
} from 'features/v2/FeaturedHeroArtwork';
import LiveSimulationPreview from 'features/v2/LiveSimulationPreview';
import V2ContentContainer from 'features/v2/V2ContentContainer';
import V2PageShell from 'features/v2/V2PageShell';
import {
  GamePlatformBadge,
  isGameSupportedOnPlatform,
  useGamePlatform,
} from 'features/v2/GamePlatformSupport';
import {
  formatWalletAddress,
  leaderboardIdentity,
  ordinalImagePath,
  productionGames,
} from 'features/v2/presentation';
import 'styles/v2.css';

const panelSx = {
  height: '100%',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: v2Tokens.radius.panel,
  backgroundColor: v2Tokens.surface,
  boxShadow: '0 18px 45px rgba(0,0,0,0.16)',
};

const SectionLabel = ({ children }) => (
  <Typography variant="overline" color="primary.main">
    {children}
  </Typography>
);

const LeaderboardRow = ({ entry, index, walletAddress }) => {
  const address = entry.ordinalAddress || entry.ethAddress;
  const isCurrentPlayer = Boolean(walletAddress && address === walletAddress);
  const image = ordinalImagePath(entry);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '28px 36px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 1,
        minHeight: 52,
        px: 1,
        py: 0.75,
        border: '1px solid',
        borderColor: isCurrentPlayer
          ? 'rgba(247,147,26,0.45)'
          : 'rgba(255,255,255,0.06)',
        borderRadius: v2Tokens.radius.nested,
        backgroundColor: isCurrentPlayer
          ? 'rgba(247,147,26,0.08)'
          : 'rgba(255,255,255,0.035)',
        transition: 'background-color 150ms ease, border-color 150ms ease',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.07)',
        },
      }}
    >
      <Typography
        className="v2-technical-label"
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color:
            index === 0
              ? 'primary.main'
              : index === 1
              ? 'rgba(255,255,255,0.75)'
              : 'text.secondary',
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </Typography>
      <Avatar
        src={image || undefined}
        alt={entry.collectionName || 'Player Ordinal'}
        variant="rounded"
        sx={{ width: 34, height: 34, bgcolor: v2Tokens.surfaceInset }}
      >
        <PersonOutline fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 12, fontWeight: 700 }}>
          {leaderboardIdentity(entry)}
          {isCurrentPlayer ? ' · You' : ''}
        </Typography>
        <Typography
          noWrap
          color="text.secondary"
          className="v2-technical-label"
          sx={{ fontSize: 9 }}
        >
          {entry.collectionName || 'Collection unavailable'}
        </Typography>
      </Box>
      <Typography
        className="v2-technical-label"
        sx={{ fontSize: 12, fontWeight: 800, color: 'primary.main' }}
      >
        {entry.score}
      </Typography>
    </Box>
  );
};

const InscriptionSandbox = ({ onOpenDetails }) => (
  <Card
    id="inscription-sandbox"
    component="section"
    aria-labelledby="sandbox-title"
    sx={{
      ...panelSx,
      p: 2.5,
      background:
        'linear-gradient(145deg, rgba(247,147,26,0.08), rgba(23,26,33,1) 55%)',
    }}
  >
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      <SectionLabel>Coming Soon</SectionLabel>
      <Typography id="sandbox-title" variant="h3">
        Inscription Sandbox
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.6 }}>
        Reserved for a future Ordinals creation and experimentation feature.
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          mt: 'auto !important',
          p: 1.25,
          border: '1px dashed rgba(255,255,255,0.12)',
          borderRadius: v2Tokens.radius.nested,
          backgroundColor: 'rgba(0,0,0,0.22)',
        }}
      >
        <Typography
          className="v2-technical-label"
          color="text.secondary"
          sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.12em' }}
        >
          Feature under construction
        </Typography>
        <Button variant="outlined" color="inherit" onClick={onOpenDetails}>
          Feature Details
        </Button>
      </Box>
    </Stack>
  </Card>
);

const HomePage = () => {
  ReactGA.initialize('G-RF6770D6JZ');
  const user = useSelector((state) => state.user);
  const walletAddress = (user && (user.ordinalAddress || user.ethAddress)) || '';
  const navigate = useNavigate();
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [scores, setScores] = useState([]);
  const [scoresStatus, setScoresStatus] = useState('loading');
  const featuredGame = productionGames[featuredIndex];
  const currentPlatform = useGamePlatform();
  const isFeaturedGameSupported = isGameSupportedOnPlatform(
    featuredGame,
    currentPlatform,
  );

  const loadScores = useCallback(async () => {
    setScoresStatus('loading');
    try {
      const response = await fetch(`${serverEnvironment}/play/scores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameName: featuredGame.id }),
      });
      if (!response.ok) throw new Error('Leaderboard request failed');
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Leaderboard response was invalid');
      setScores(data.slice(0, 4));
      setScoresStatus('success');
    } catch {
      setScores([]);
      setScoresStatus('error');
    }
  }, [featuredGame.id]);

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    loadScores();
  }, [loadScores]);

  const launchGame = (game) => {
    if (!isGameSupportedOnPlatform(game, currentPlatform)) return;

    ReactGA.event({
      category: 'routing',
      action: 'game_page',
      label: game.title,
    });
    navigate(game.route);
  };

  const showNextGame = () => {
    setFeaturedIndex((current) => (current + 1) % productionGames.length);
  };

  return (
    <V2PageShell>
      <V2ContentContainer
        component="main"
        sx={{ py: { xs: 3, md: 4 } }}
      >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              justifyContent: 'space-between',
              gap: 2,
              pb: 3,
              mb: 4,
              borderBottom: '1px solid rgba(255,255,255,0.05)',
            }}
          >
            <Box>
              <SectionLabel>ARCADE TERMINAL V2</SectionLabel>
              <Typography
                component="h1"
                sx={{
                  mt: 0.5,
                  color: '#fff',
                  fontSize: 30,
                  fontWeight: 700,
                  fontStyle: 'italic',
                  lineHeight: 1.2,
                  letterSpacing: '-0.025em',
                  textTransform: 'uppercase',
                }}
              >
                COMMAND CENTER
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ maxWidth: 512, mt: 0.5, fontSize: 12, lineHeight: 1.625 }}
              >
                Enter the world’s first Bitcoin Ordinal PFP gaming platform—connect your wallet, play as your Ordinal, and compete for high scores.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8} sx={{ order: 1 }}>
              <Card
                variant="v2-raised"
                component="section"
                aria-labelledby="featured-game-title"
                sx={{
                  ...panelSx,
                  ...featuredHeroPanelSx,
                  position: 'relative',
                  overflow: 'hidden',
                  background:
                    'radial-gradient(circle at 2% 28%, rgba(13,148,136,0.17), transparent 46%), radial-gradient(circle at 96% 72%, rgba(126,34,206,0.17), transparent 48%), linear-gradient(135deg, #111820 0%, #171625 100%)',
                  boxShadow: '0 20px 48px rgba(0,0,0,0.22)',
                }}
              >
                <FeaturedHeroArtwork />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    background:
                      'linear-gradient(120deg, rgba(7,9,13,0.28), transparent 54%, rgba(7,9,13,0.22))',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={featuredHeroContentSx}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      minWidth: 0,
                    }}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        useFlexGap
                        spacing={1}
                        sx={{ mb: { xs: 2.5, lg: 3 } }}
                      >
                        <Chip
                          label={featuredGame.genre}
                          color="primary"
                          sx={{
                            color: '#07090D',
                            borderRadius: v2Tokens.radius.control,
                            backgroundColor: 'primary.main',
                          }}
                        />
                        <Chip
                          label={featuredGame.mode}
                          sx={{
                            color: '#E5E5E5',
                            border: '1px solid rgba(255,255,255,0.10)',
                            borderRadius: v2Tokens.radius.control,
                            backgroundColor: 'rgba(7,9,13,0.62)',
                          }}
                        />
                        <GamePlatformBadge game={featuredGame} />
                      </Stack>
                      <Typography
                        id="featured-game-title"
                        component="h2"
                        sx={{
                          maxWidth: '100%',
                          color: '#F5F5F4',
                          fontSize: {
                            xs: 'clamp(2.5rem, 14vw, 3.25rem)',
                            sm: '3.5rem',
                            lg: 'clamp(4rem, 5.4vw, 5.125rem)',
                          },
                          fontWeight: 900,
                          fontStyle: 'italic',
                          lineHeight: 0.9,
                          letterSpacing: '-0.055em',
                          overflowWrap: 'anywhere',
                          textTransform: 'uppercase',
                        }}
                      >
                        {featuredGame.title}
                      </Typography>
                      <Typography
                        color="text.secondary"
                        sx={{
                          mt: { xs: 2, lg: 2.5 },
                          maxWidth: 510,
                          fontSize: { xs: 13, sm: 14 },
                          lineHeight: 1.7,
                        }}
                      >
                        {featuredGame.description}
                      </Typography>
                    </Box>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.25}
                      sx={featuredHeroActionsSx}
                    >
                      <Button
                        variant="contained"
                        endIcon={<ArrowForward />}
                        onClick={() => launchGame(featuredGame)}
                        disabled={!isFeaturedGameSupported}
                        aria-label={
                          isFeaturedGameSupported
                            ? `Play ${featuredGame.title}`
                            : `${featuredGame.title}: Desktop Only`
                        }
                        sx={{
                          minHeight: 50,
                          px: { xs: 3.5, lg: 2.25, xl: 3.5 },
                          color: '#07090D',
                          backgroundColor: '#fff',
                          boxShadow: '0 10px 24px rgba(0,0,0,0.22)',
                          '&:hover': { backgroundColor: '#E5E5E5' },
                          '&.Mui-disabled': {
                            color: 'rgba(255,255,255,0.62)',
                            backgroundColor: 'rgba(255,255,255,0.10)',
                          },
                        }}
                      >
                        {isFeaturedGameSupported ? 'Play now' : 'Desktop only'}
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        onClick={showNextGame}
                        sx={{
                          minHeight: 50,
                          px: { xs: 3, lg: 1.75, xl: 3 },
                          backgroundColor: 'rgba(255,255,255,0.06)',
                        }}
                      >
                        Next game
                      </Button>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: { sm: 420, lg: 'none' },
                      minWidth: 0,
                      alignSelf: 'start',
                      justifySelf: { sm: 'end', lg: 'stretch' },
                    }}
                  >
                    <LiveSimulationPreview game={featuredGame} />
                  </Box>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={4} sx={{ order: 2 }}>
              <Card
                component="section"
                aria-labelledby="scoreboard-title"
                sx={{ ...panelSx, minHeight: 420, p: 2.5 }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Box>
                    <SectionLabel>Live Production Data</SectionLabel>
                    <Typography id="scoreboard-title" variant="h3" sx={{ mt: 0.5 }}>
                      <EmojiEventsOutlined sx={{ mr: 1, color: 'primary.main', verticalAlign: 'text-bottom' }} />
                      Leaderboard
                    </Typography>
                  </Box>
                  <Chip label={featuredGame.title} variant="outlined" />
                </Stack>

                {scoresStatus === 'loading' && (
                  <Stack spacing={1} aria-label="Loading leaderboard">
                    <LinearProgress color="primary" sx={{ mb: 1 }} />
                    {[0, 1, 2, 3].map((item) => (
                      <Skeleton key={item} variant="rounded" height={52} />
                    ))}
                  </Stack>
                )}

                {scoresStatus === 'error' && (
                  <Box
                    role="alert"
                    sx={{
                      p: 2,
                      border: '1px solid rgba(239,68,68,0.32)',
                      borderRadius: v2Tokens.radius.nested,
                      backgroundColor: 'rgba(127,29,29,0.20)',
                    }}
                  >
                    <Typography color="error.light" sx={{ fontSize: 12, fontWeight: 700 }}>
                      Scores are unavailable right now.
                    </Typography>
                    <Button color="error" variant="outlined" size="small" onClick={loadScores} sx={{ mt: 1.5 }}>
                      Retry
                    </Button>
                  </Box>
                )}

                {scoresStatus === 'success' && scores.length === 0 && (
                  <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 245, textAlign: 'center' }}>
                    <EmojiEventsOutlined color="disabled" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>No scores yet</Typography>
                    <Typography color="text.secondary" sx={{ maxWidth: 230, fontSize: 11 }}>
                      Completed scoreboard entries will appear here when the production API returns them.
                    </Typography>
                  </Stack>
                )}

                {scoresStatus === 'success' && scores.length > 0 && (
                  <Stack spacing={1}>
                    {scores.map((entry, index) => (
                      <LeaderboardRow
                        key={`${entry.ordinalAddress || entry.ethAddress || 'anonymous'}-${entry.score}-${index}`}
                        entry={entry}
                        index={index}
                        walletAddress={walletAddress}
                      />
                    ))}
                  </Stack>
                )}

                <Button
                  fullWidth
                  variant="text"
                  color="primary"
                  endIcon={<ChevronRight />}
                  onClick={() => navigate('/leaderboard')}
                  sx={{ mt: 1.5 }}
                >
                  View full leaderboard
                </Button>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={3} sx={{ order: { xs: 3, md: 4, lg: 3 } }}>
              <Card
                component="section"
                aria-labelledby="ordinal-panel-title"
                sx={{
                  ...panelSx,
                  minHeight: 210,
                  p: 2.5,
                  background: 'linear-gradient(145deg, #1B2838, #171A21 72%)',
                }}
              >
                <SectionLabel>Selected Ordinal</SectionLabel>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'rgba(0,0,0,0.35)',
                      border: '1px dashed rgba(247,147,26,0.35)',
                    }}
                  >
                    {walletAddress ? <ImageNotSupportedOutlined /> : <PersonOutline />}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography id="ordinal-panel-title" sx={{ fontWeight: 800 }}>
                      {walletAddress ? 'No Ordinal selected' : 'Wallet not connected'}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 11, lineHeight: 1.55 }}>
                      {walletAddress
                        ? 'Choose a real available character in the existing game launch flow.'
                        : 'Use the wallet control above to access your available characters.'}
                    </Typography>
                  </Box>
                </Stack>
                {walletAddress && (
                  <Chip
                    label={formatWalletAddress(walletAddress)}
                    variant="outlined"
                    sx={{ mt: 2 }}
                  />
                )}
              </Card>
            </Grid>

            <Grid item xs={12} md={12} lg={6} sx={{ order: { xs: 4, md: 3, lg: 4 } }}>
              <Card component="section" aria-labelledby="game-library-title" sx={{ ...panelSx, minHeight: 210, p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Box>
                    <SectionLabel>Game Library</SectionLabel>
                    <Typography id="game-library-title" variant="h3" sx={{ mt: 0.4 }}>
                      Most Popular Games
                    </Typography>
                  </Box>
                  <CasinoOutlined color="primary" />
                </Stack>
                <Box
                  sx={{
                    display: 'grid',
                    gridAutoFlow: { xs: 'column', sm: 'row' },
                    gridAutoColumns: { xs: 'minmax(145px, 72%)', sm: 'auto' },
                    gridTemplateColumns: { sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.25,
                    overflowX: { xs: 'auto', sm: 'visible' },
                    pb: { xs: 1, sm: 0 },
                  }}
                >
                  {productionGames.map((game) => {
                    const isSupported = isGameSupportedOnPlatform(
                      game,
                      currentPlatform,
                    );

                    return (
                      <CardActionArea
                        className="v2-game-card"
                        key={game.id}
                        aria-label={
                          isSupported
                            ? `Play ${game.title}`
                            : `${game.title}: Desktop Only`
                        }
                        disabled={!isSupported}
                        onClick={() => launchGame(game)}
                        sx={{
                          position: 'relative',
                          minWidth: 0,
                          minHeight: 125,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor:
                            featuredGame.id === game.id
                              ? 'primary.main'
                              : 'rgba(255,255,255,0.07)',
                          borderRadius: v2Tokens.radius.nested,
                          boxShadow:
                            featuredGame.id === game.id
                              ? '0 0 12px rgba(247,147,26,0.25)'
                              : 'none',
                          '&:hover': { borderColor: 'rgba(247,147,26,0.55)' },
                          '&.Mui-disabled': { opacity: 1 },
                        }}
                      >
                        <Box
                          component="img"
                          className="v2-game-card-art"
                          src={game.artwork}
                          alt=""
                          aria-hidden="true"
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            opacity: 0.32,
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            background: 'linear-gradient(0deg, rgba(7,9,13,0.96), rgba(7,9,13,0.12))',
                          }}
                        />
                        <Box sx={{ position: 'absolute', zIndex: 2, top: 10, right: 10 }}>
                          <GamePlatformBadge game={game} />
                        </Box>
                        <Box sx={{ position: 'relative', zIndex: 1, p: 1.5, pt: 8 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase' }}>
                            {game.title}
                          </Typography>
                          <Typography variant="overline" color="text.secondary">
                            {game.genre}
                          </Typography>
                        </Box>
                      </CardActionArea>
                    );
                  })}
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6} lg={3} sx={{ order: 5 }}>
              <InscriptionSandbox onOpenDetails={() => navigate('/inscription-sandbox')} />
            </Grid>
          </Grid>
      </V2ContentContainer>
    </V2PageShell>
  );
};

export default HomePage;
